import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {Box, Button, HStack, Stack, Text, VStack} from '@chakra-ui/react3'
import AddToBabylist from '../../../../components/add-to-babylist'
import AddToCartCTA from '../../../../components/add-to-cart-cta'
import BVProductRating from '../../../../components/bazaarvoice/rating'
import {hasBVProductRating} from '../../../../components/bazaarvoice/utils'
import ProductDisplayPrice from '../../../../components/product-display-price'
import {useProductConfiguratorControl} from '../../../../contexts/product-configurator'
import useAR from '../../../../hooks/use-ar'
import useEffectEvent from '../../../../hooks/use-effect-event'
import {useConfiguratorHighlights} from '../../../../hooks/useScapiCustomApi'
import {
    _3DViewIcon,
    PaintBoardIcon,
    ResourcesAddIcon,
    TextCircleIcon
} from '../../../../leap/components/icons'
import Chakra3Provider from '../../../../leap/theme'
import {getMasterProductId, isProductSet} from '../../../../utils/product-utils'
import CockpitTags from '../cockpit-tags'
import CockpitVariations from '../cockpit-variations'
import CustomizablePsdpSetProductTile from '../customizable-psdp-set-product-tile'
import ProductAvailability from '../product-availability'
import PromotionBanner from './promotion-banner'
import Gallery from './gallery'

const HighlightItem = ({children}) => (
    <HStack
        gap="spacing-12"
        align="center"
        color="text.primary"
        textStyle="heading.small"
        _icon={{width: '1rem', height: '1rem'}}
    >
        {children}
    </HStack>
)
HighlightItem.propTypes = {
    children: PropTypes.node
}

const XleapCockpit = ({
    derivedProduct,
    product,
    isGetPropsLoading,
    isMasterLoading,
    isPromotionsLoading,
    isVariantLoading,
    onAddToCartClick,
    onMasterChange,
    onRatingsClick,
    onRecommendedProductClick,
    onShowAllColorsClick,
    onVariantChange
}) => {
    const {formatMessage} = useIntl()
    const configuratorConfig = product?.c_configuratorConfig
    const displayTemplate = product?.c_displayTemplate
    const hasProductRating = useMemo(() => hasBVProductRating(product), [product])
    const {ARModel} = derivedProduct
    const ARProps = useAR(ARModel)
    const highlightsQuery = useConfiguratorHighlights(
        {
            parameters: {
                configId: configuratorConfig,
                productId: getMasterProductId(product) || product?.id
            }
        },
        {
            enabled: displayTemplate === 'xpdp-configurator'
        }
    )
    const hasHighlights =
        highlightsQuery.data?.numberOfColors > 0 ||
        highlightsQuery.data?.numberOfAccessories > 0 ||
        highlightsQuery.data?.hasEngraving

    // activate configurator
    const setConfiguratorState = useProductConfiguratorControl()
    const onStartConfigurator = useEffectEvent(() => {
        setConfiguratorState(true)
    })

    return (
        <Chakra3Provider theme="light">
            <Box
                data-testid={`cockpit-${displayTemplate}`}
                display={{
                    base: 'flex',
                    s: 'grid'
                }}
                flexDirection="column"
                paddingBlock="spacing-24"
                paddingInline={{base: 0, s: 'spacing-32'}}
                gapY={'spacing-32'}
                gapX={{base: 'spacing-32', s: 'spacing-80'}}
                align="stretch"
                layerStyle="container.page"
                gridTemplateAreas='"promotion promotion" "gallery info" "gallery extra" "gallery order"'
                gridTemplateColumns="1fr 25rem"
                gridTemplateRows="auto auto auto 1fr"
            >
                <Box gridArea="promotion">
                    <PromotionBanner product={product} />
                </Box>
                <Box gridArea="gallery">
                    <Gallery product={product} derivedProduct={derivedProduct}></Gallery>
                </Box>

                <Box data-testid="main-info" paddingInline={{base: 'spacing-24', s: 0}}>
                    <VStack gap="spacing-20" align="stretch">
                        <VStack gap="0" align="stretch">
                            <Box>
                                <Box float="right">
                                    <CockpitTags product={product} />
                                </Box>

                                <Text as="h1">
                                    {product?.c_suitableFromAgeinMonths != null ||
                                    product?.c_suitableToAgeinMonths != null ? (
                                        <Text
                                            as="span"
                                            display="block"
                                            marginBlockEnd="spacing-20"
                                            color="text.brand-default"
                                            textStyle="body.small"
                                        >
                                            {[
                                                'TODO:',
                                                'STOK-15615',
                                                product?.c_suitableFromAgeinMonths != null &&
                                                    `from ${product?.c_suitableFromAgeinMonths} months`,
                                                product?.c_suitableToAgeinMonths != null &&
                                                    `to ${product?.c_suitableToAgeinMonths} months`
                                            ]
                                                .filter(Boolean)
                                                .join(' ')}
                                        </Text>
                                    ) : null}

                                    <Text
                                        as="span"
                                        display="inline-block"
                                        color="text.primary"
                                        textStyle="heading.xlarge"
                                    >
                                        {product.name}
                                    </Text>
                                </Text>
                            </Box>

                            <Text
                                as="span"
                                display="inline-block"
                                color="text.tertiary"
                                textStyle="body.default"
                            >
                                {product.c_slogan}
                            </Text>
                        </VStack>

                        {hasProductRating && (
                            <BVProductRating
                                product={product}
                                variant="cockpit"
                                onRatingsClick={onRatingsClick}
                            />
                        )}
                    </VStack>
                </Box>

                <VStack
                    gridArea="extra"
                    gap="spacing-24"
                    align="stretch"
                    paddingInline={{base: 'spacing-24', s: 0}}
                >
                    {displayTemplate === 'xpdp' &&
                        (isProductSet(product) ? (
                            product.setProducts.map((setProduct) => (
                                <CustomizablePsdpSetProductTile
                                    key={setProduct.setProductId}
                                    product={setProduct}
                                />
                            ))
                        ) : (
                            <CockpitVariations
                                product={product}
                                isMasterLoading={isMasterLoading}
                                isVariantLoading={isVariantLoading}
                                onMasterChange={onMasterChange}
                                onVariantChange={onVariantChange}
                                onShowAllColorsClick={onShowAllColorsClick}
                            />
                        ))}

                    <Text color="text.secondary" textStyle="body.small">
                        {product.shortDescription}
                    </Text>

                    {displayTemplate === 'xpdp-configurator' && hasHighlights && (
                        <VStack gap="spacing-12" align="stretch">
                            {highlightsQuery.data?.numberOfColors > 0 && (
                                <HighlightItem>
                                    <PaintBoardIcon role="presentation" />

                                    <FormattedMessage
                                        id="product_detail.number_of_colors"
                                        defaultMessage="{value} beautiful colors"
                                        values={{
                                            value: highlightsQuery.data?.numberOfColors
                                        }}
                                    />
                                </HighlightItem>
                            )}

                            {highlightsQuery.data?.numberOfAccessories > 0 && (
                                <HighlightItem>
                                    <ResourcesAddIcon role="presentation" />

                                    <FormattedMessage
                                        id="product_detail.number_of_accessories"
                                        defaultMessage="{value} accessories available"
                                        values={{
                                            value: highlightsQuery.data?.numberOfAccessories
                                        }}
                                    />
                                </HighlightItem>
                            )}

                            {highlightsQuery.data?.hasEngraving && (
                                <HighlightItem>
                                    <TextCircleIcon role="presentation" />

                                    <FormattedMessage
                                        id="product_detail.has_engraving"
                                        defaultMessage="Free engraving"
                                    />
                                </HighlightItem>
                            )}
                        </VStack>
                    )}

                    {ARProps?.hasAR && (
                        <Button
                            as="a"
                            variant="secondaryOutline"
                            display={{base: 'inline-flex', s: 'none'}}
                            href={ARProps?.href || '#'}
                            rel="ar"
                            onClick={ARProps?.onARLinkClick}
                            aria-label={formatMessage({
                                defaultMessage: 'Open AR view',
                                id: 'product_detail.button.open_ar_view'
                            })}
                        >
                            <span>
                                <FormattedMessage
                                    id="product_detail.button.view_in_space"
                                    defaultMessage="View in space"
                                />
                            </span>
                            <_3DViewIcon role="presentation" />
                        </Button>
                    )}
                </VStack>

                <VStack
                    gridArea="order"
                    gap={{base: 'spacing-24', s: 'spacing-16'}}
                    align="stretch"
                    position={{base: 'sticky', s: 'static'}}
                    zIndex="sticky"
                    bottom="0"
                    paddingInline={{base: 'spacing-24', s: 0}}
                    paddingBlock="spacing-24"
                    backgroundColor="primary.base.white"
                    borderTop="1px solid"
                    borderTopColor="border.subtle"
                    backdropFilter="auto"
                    backdropBlur="md"
                >
                    <HStack justify="space-between" align="flex-end">
                        <ProductDisplayPrice
                            product={product}
                            loading={isGetPropsLoading || isPromotionsLoading || isVariantLoading}
                            variant="pdp"
                        />

                        <ProductAvailability
                            loading={isVariantLoading}
                            flexShrink="0"
                            product={product}
                        />
                    </HStack>

                    {(() => {
                        switch (displayTemplate) {
                            case 'xpdp':
                                return (
                                    <Stack
                                        gap="spacing-8"
                                        direction={{base: 'column', s: 'row'}}
                                        align={{base: 'stretch', s: 'center'}}
                                    >
                                        <AddToBabylist product={product} />
                                        <AddToCartCTA
                                            isLoading={
                                                isGetPropsLoading ||
                                                isPromotionsLoading ||
                                                isVariantLoading
                                            }
                                            product={product}
                                            onClick={onAddToCartClick}
                                            onRecommendedProductClick={onRecommendedProductClick}
                                        />
                                    </Stack>
                                )
                            case 'xpdp-configurator':
                                return (
                                    <Button
                                        variant="secondaryFilled"
                                        width="auto"
                                        size="lg"
                                        onClick={onStartConfigurator}
                                    >
                                        <span>
                                            <FormattedMessage
                                                id="product_detail.cta.customize"
                                                defaultMessage="Customize & Buy"
                                            />
                                        </span>
                                        <Box
                                            className="icon"
                                            role="presentation"
                                            borderRadius="99px"
                                            background="conic-gradient(from 180deg at 50% 50%, #FFA604 0deg, #FF4F45 72deg, #BA5DD2 144deg, #1299F5 216deg, #51CC26 288deg, #FFD203 360deg), #B0B0B2"
                                        />
                                    </Button>
                                )
                            default:
                                throw new Error(`Invalid display template: ${displayTemplate}`)
                        }
                    })()}
                </VStack>
            </Box>
        </Chakra3Provider>
    )
}

XleapCockpit.propTypes = {
    derivedProduct: PropTypes.object,
    product: PropTypes.object,
    isGetPropsLoading: PropTypes.bool,
    isMasterLoading: PropTypes.bool,
    isPromotionsLoading: PropTypes.bool,
    isVariantLoading: PropTypes.bool,
    onAddToCartClick: PropTypes.func,
    onMasterChange: PropTypes.func,
    onRatingsClick: PropTypes.func,
    onRecommendedProductClick: PropTypes.func,
    onShowAllColorsClick: PropTypes.func,
    onVariantChange: PropTypes.func
}

export default XleapCockpit
